import { ChevronRightIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import Anchor from 'components/atoms/Anchor';
import { Menu } from 'components/atoms/Menu';
import dayjs from 'dayjs';
import { useIntercom } from 'hooks/intercom.hook';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WEB_MENU_CLICKEDKNOWLEDGECENTER,
  WEB_MENU_CLICKEDSUPPORT,
} from 'utils/amplitude/events/global.amplitude';
import {
  DoubleChatIcon,
  HeartIcon,
  InfoIconOutline,
} from 'utils/icons/figmaIcons';
import convertMarkdown from 'utils/markdown';
import IntercomUnreadCount from './CountBadges/IntercomUnreadCount.component';
import IntercomUnreadDot from './CountBadges/IntercomUnreadDot.component';
import NavigationItem from './NavigationItem.component';
import { SidebarNavigationItemTypesEnum } from './types';

const useBusinessHours = () => {
  const businessHours = { start: 9, end: 17 };
  const now = dayjs().tz('Europe/Brussels');
  const currentHour = now.hour();
  const currentDay = now.day(); // 0 is Sunday, 6 is Saturday

  const isBusinessDay = currentDay >= 1 && currentDay <= 5; // Monday to Friday
  const isOutsideBusinessHours =
    !isBusinessDay ||
    currentHour < businessHours.start ||
    currentHour >= businessHours.end;

  if (!isOutsideBusinessHours) {
    return { isOutsideBusinessHours, hoursLeft: 0 };
  }

  // If it's not a business day, calculate hours until next business day at 9 AM
  if (!isBusinessDay) {
    const daysUntilNextBusinessDay = currentDay === 6 ? 2 : 1; // 6 is Saturday, so 2 days until Monday, 1 day until Tuesday, etc.
    const hoursLeft =
      daysUntilNextBusinessDay * 24 - currentHour + businessHours.start;

    return { isOutsideBusinessHours, hoursLeft };
  }

  // if it's a business day, but outside business hours, calculate hours until next business day at 9 AM
  const hoursLeft =
    currentHour >= businessHours.end
      ? 24 - currentHour + businessHours.start
      : businessHours.start - currentHour;

  return { isOutsideBusinessHours, hoursLeft };
};

const SupportMenu: FC = () => {
  const { t, i18n } = useTranslation();
  const { showIntercom } = useIntercom();

  const { isOutsideBusinessHours, hoursLeft } = useBusinessHours();

  return (
    <Menu
      buttonProps={{ id: 'SUPPORT_MENU', className: 'w-full' }}
      position="top"
      trigger={(api) => {
        const isActive = api.open;
        return (
          <NavigationItem
            active={isActive}
            titleContainerClassName="flex-1"
            item={{
              title: '',
              renderTitle: () => (
                <span
                  className={classNames(
                    'flex flex-1 w-full items-center justify-between',
                    isActive ? '!text-primary' : '',
                  )}
                >
                  <span className="flex-1">
                    {t('sidebar.support')}
                    <IntercomUnreadDot className="align-top inline-block" />
                  </span>
                  <span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </span>
                </span>
              ),
              Icon: HeartIcon,
              type: SidebarNavigationItemTypesEnum.support,
            }}
          />
        );
      }}
      rootClassName="w-full"
      contentWrapperClassName="left-[13rem] !ring-0 !px-1 !py-1 !rounded-xl !w-60"
    >
      <Menu.Group className="!p-0">
        <Menu.Button
          className="flex w-full items-center rounded-md px-2 py-2 text-sm font-semibold hover:!bg-primary-50 gap-2 !text-primary-800 hover:text-primary group"
          tracingEvent={WEB_MENU_CLICKEDSUPPORT}
          onClick={showIntercom}
        >
          <span className="w-6 group-hover:!text-primary">
            <DoubleChatIcon className="w-6" />
          </span>
          <span className="flex flex-col text-left">
            <span className="font-semibold block text-md group-hover:!text-primary flex gap-2 items-center">
              {t('sidebar.support.human.title')}
              <IntercomUnreadCount className="!h-4 !w-4 !text-[0.6rem]" />
            </span>
            <span className="text-xs text-primary-700 font-medium">
              {convertMarkdown(t('sidebar.support.human.description'))}
            </span>
            {isOutsideBusinessHours && (
              <span className="text-xs text-primary-700 mt-1 font-medium">
                {t('sidebar.support.human.business_hours', { hoursLeft })}
              </span>
            )}
          </span>
        </Menu.Button>
      </Menu.Group>
      <Menu.Group className="!p-0 !border-t-0">
        <Anchor
          href={`https://help.accountable.eu/${i18n.language}`}
          className="flex w-full rounded-md px-2 py-2 text-sm font-semibold hover:!bg-primary-50 gap-2 !text-primary-800 hover:text-primary !items-start group"
          target="_blank"
          rel="noreferrer"
          tracingEvent={WEB_MENU_CLICKEDKNOWLEDGECENTER}
        >
          <span className="w-6 group-hover:!text-primary">
            <InfoIconOutline className="w-6" />
          </span>
          <span className="flex flex-col text-left">
            <span className="font-semibold block text-md group-hover:!text-primary flex gap-2 items-center">
              {t('sidebar.support.knowledge_center.title')}
            </span>
            <span className="text-xs text-primary-700 font-medium">
              {convertMarkdown(
                t('sidebar.support.knowledge_center.description'),
              )}
            </span>
          </span>
        </Anchor>
      </Menu.Group>
    </Menu>
  );
};

export default SupportMenu;
